import {
  Button,
  ButtonSize,
  ButtonVariant,
} from "@amzn/stencil-react-components/button";
import { Row, View } from "@amzn/stencil-react-components/layout";
import { TextArea } from "@amzn/stencil-react-components/text-area";
import IconSendMedium from "@amzn/stencil-react-icons/icons/icon-send-medium";
import { useState } from "react";
import { useIntl } from "react-intl";

/**
 * Message composer: a single bordered surface holding the input and send button
 * on one line. Send only — no attachments, no mode switch, no dictation.
 *
 * Owns the draft text so the parent only deals with completed messages.
 */
export const ChatComposer = ({
  onSend,
  disabled = false,
}: {
  onSend: (message: string) => void;
  disabled?: boolean;
}): JSX.Element => {
  const intl = useIntl();
  const [draft, setDraft] = useState("");

  const canSend = !!draft.trim() && !disabled;

  const send = () => {
    if (!canSend) return;
    onSend(draft);
    setDraft("");
  };

  return (
    <View
      className="chatbot-composer"
      backgroundColor="color.surface.bg-default"
      border="1px solid color.border.primary"
      borderRadius="dimensions.border.radius.200"
      // Asymmetric by design: 8 top / 8 sides / 12 bottom.
      padding={{ top: "S200", left: "S200", right: "S200", bottom: "S250" }}
    >
      <Row gridGap="S200" alignItems="flex-end">
        <TextArea
          id="chatbot-input"
          className="chatbot-composer__input"
          aria-label={intl.formatMessage({ id: "chatbot-input-label" })}
          value={draft}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setDraft(e.target.value)
          }
          onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            // Enter sends; Shift+Enter inserts a newline.
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder={intl.formatMessage({ id: "chatbot-input-placeholder" })}
          disabled={disabled}
          flex={1}
          rows={1}
          resize="none"
          dataTestId="chatbotInput"
        />
        {/*
          "Embedded AI Action/Primary" in the design: Stencil's small primary
          button, which is already the submit orange, made circular in CSS.
        */}
        <Button
          variant={ButtonVariant.Primary}
          size={ButtonSize.Small}
          className="chatbot-composer__send"
          onClick={send}
          disabled={!canSend}
          aria-label={intl.formatMessage({ id: "chatbot-send-label" })}
          icon={<IconSendMedium aria-hidden />}
          dataTestId="chatbotSendButton"
        />
      </Row>
    </View>
  );
};
